import { buildAbsentStudentRows } from "@/artifacts/admin/utils/dashboard/buildAbsentStudentRows";
import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { Input } from "@/components/ui/Input/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { numberInStringArrayFilter } from "@/utils/filters";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";
import { format } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Filter,
  UserRoundCog,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { AdminAbsentStudent } from "@/types/tables";

const buildColumns = (
  onOpen: (studentId: string) => void,
): ColumnDef<AdminAbsentStudent>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Student{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    accessorKey: "email",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Email{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    accessorKey: "gradeLevel",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Grade Level{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: numberInStringArrayFilter,
  },
  {
    accessorKey: "explorationName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Exploration{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => (
      <HStack justify="flex-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon-small" onClick={() => onOpen(row.original._id)}>
              <UserRoundCog />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Manage {row.original.name}</TooltipContent>
        </Tooltip>
      </HStack>
    ),
    enableSorting: false,
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: AdminAbsentStudent[];
  columns: ColumnDef<AdminAbsentStudent>[];
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  // Grade Filtering

  const gradeCol = table.getColumn("gradeLevel");
  const selectedGrade =
    (gradeCol?.getFilterValue() as string[] | undefined) ?? [];

  const grades = useMemo(() => {
    const set = new Set<string>();
    for (const d of data) if (d.gradeLevel) set.add(String(d.gradeLevel));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const toggleGrade = (grade: string) => {
    const next = selectedGrade.includes(grade)
      ? selectedGrade.filter((s) => s !== grade)
      : [...selectedGrade, grade];

    gradeCol?.setFilterValue(next);
  };

  const clearGradeFilter = () => {
    gradeCol?.setFilterValue([]);
  };

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  return (
    <>
      <HStack align="center" justify="space-between" wrap>
        <HStack align="center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                style={{
                  height: "fit-content",
                  padding: "var(--space-xxs) var(--space-xs)",
                }}
              >
                <Filter /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Grades</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {grades.map((g) => (
                    <DropdownMenuCheckboxItem
                      key={g}
                      checked={selectedGrade.includes(g)}
                      onCheckedChange={() => toggleGrade(g)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {g}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearGradeFilter();
                    }}
                    disabled={selectedGrade.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <p
            style={{
              color: "var(--muted-foreground)",
            }}
          >
            {filteredCount} of {totalCount} students
          </p>
        </HStack>
        <div style={{ width: "240px", maxWidth: "100%" }}>
          <Input
            placeholder="Search students..."
            value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
            onChange={(e) =>
              table.getColumn("name")?.setFilterValue(e.target.value)
            }
          />
        </div>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

const AbsentStudentsTable = ({
  students,
  rotationDate,
  sessionName,
  onOpen,
}: {
  students: any[];
  rotationDate: string;
  sessionName: string;
  onOpen: (studentId: string) => void;
}) => {
  const rows = useMemo(() => buildAbsentStudentRows(students), [students]);
  const columns = useMemo(() => buildColumns(onOpen), [onOpen]);

  useEffect(() => {
  }, [students]);

  return (
    <VStack gap="1rem">
      <h4
        style={{
          fontSize: "var(--text-md)",
          lineHeight: "var(--line-height-md)",
          fontWeight: "var(--font-bold)",
        }}
      >
        Absent Students — {format(rotationDate, "MMMM dd, yyyy")} -{" "}
        {sessionName}
      </h4>
      <DashboardCard>
        <DataTable data={rows} columns={columns} />
      </DashboardCard>
    </VStack>
  );
};

export { AbsentStudentsTable };
